
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Bot } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Bot as BotType, ExchangePlatform, ColorScheme } from '@/types/trader';
import { z } from 'zod';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ColorSchemeSelector } from '@/components/dashboard/ColorSchemeSelector';

// Create schema for edit form
const editBotSchema = z.object({
  name: z.string().min(3, { message: 'Bot name must be at least 3 characters' }).max(50),
  description: z.string().min(10, { message: 'Description must be at least 10 characters' }).max(500),
  exchanges: z.array(z.string()).min(1, { message: 'Select at least one exchange' }),
  colorScheme: z.string().optional(),
});

type EditBotFormValues = z.infer<typeof editBotSchema>;

interface EditBotDialogProps {
  isOpen: boolean;
  onClose: () => void;
  bot: BotType;
}

export const EditBotDialog = ({ isOpen, onClose, bot }: EditBotDialogProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Available color schemes
  const colorSchemes: { value: ColorScheme | string; label: string }[] = [
    { value: 'royal_purple', label: 'Royal Purple' },
    { value: 'ocean_blue', label: 'Ocean Blue' },
    { value: 'emerald_green', label: 'Emerald Green' },
    { value: 'ruby_red', label: 'Ruby Red' },
    { value: 'amber_gold', label: 'Amber Gold' },
    { value: 'midnight_black', label: 'Midnight Black' },
    { value: 'coral_orange', label: 'Coral Orange' },
  ];

  // Initialize form with current bot values
  const form = useForm<EditBotFormValues>({
    resolver: zodResolver(editBotSchema),
    defaultValues: {
      name: bot.name,
      description: bot.description,
      exchanges: Array.isArray(bot.exchange) ? bot.exchange : [bot.exchange].filter(Boolean),
      colorScheme: bot.color_scheme || 'royal_purple',
    },
  });

  // Mutation to update bot
  const updateBotMutation = useMutation({
    mutationFn: async (data: EditBotFormValues) => {
      const { error } = await supabase
        .from('bots')
        .update({
          name: data.name,
          description: data.description,
          exchange: data.exchanges,
          color_scheme: data.colorScheme,
          last_updated: new Date().toISOString(),
        })
        .eq('id', bot.id);

      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bot', bot.id] });
      toast({
        title: 'Bot updated',
        description: 'The bot details have been updated successfully.',
      });
      onClose();
    },
    onError: (error) => {
      console.error('Error updating bot:', error);
      toast({
        title: 'Update failed',
        description: 'Failed to update bot. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Handle form submission
  const onSubmit = (data: EditBotFormValues) => {
    updateBotMutation.mutate(data);
  };

  // Available exchanges
  const exchanges: ExchangePlatform[] = [
    'Ctrader', 'MT4', 'MT5', 'Binance', 'OKX', 'BingX', 'Bybit', 'Bitget'
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Edit Bot
          </DialogTitle>
          <DialogDescription>
            Update your bot's information
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bot Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter a name for your bot" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe what your bot does"
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="exchanges"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Exchanges</FormLabel>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {exchanges.map((exchange) => (
                      <Badge
                        key={exchange}
                        variant={field.value.includes(exchange) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => {
                          const updatedValue = field.value.includes(exchange)
                            ? field.value.filter(e => e !== exchange)
                            : [...field.value, exchange];
                          field.onChange(updatedValue);
                        }}
                      >
                        {exchange}
                      </Badge>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="colorScheme"
              render={({ field }) => (
                <ColorSchemeSelector
                  colorSchemes={colorSchemes}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateBotMutation.isPending}
              >
                {updateBotMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
